import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { isoDate } from "./primitives"
import { categoryTypeSchema } from "./product-facts"

// ── Copy generation ───────────────────────────────────────────────────────
//
// Copy contracts are deliberately marketplace-neutral. The CLI emits and
// consumes generic { title, shortDescription, longDescription } records; the
// marketplace adapter (read from `apps/api/data/marketplaces/<id>/config.json`
// → `copy.attributeCodes`) maps those generic keys to the marketplace-native
// attribute codes when applying.

// The canonical, marketplace-neutral set of copy field roles. One source of
// truth for which fields the pipeline can generate, select, and write — the
// `--fields` selector, the dynamic output schema, the prompt-tag gating, and
// the apply mapping all key off this. Declared `as const` so a typo at any call
// site is a compile error rather than a silently-unequal string. The copy
// attribute schemas and the role → attribute-code map are DERIVED from this
// list (see below), so they cannot drift from it.
export const COPY_FIELD_ROLES = [
  "title",
  "shortDescription",
  "longDescription",
  "composition",
  "useRestriction",
  "storageAdvice",
  "sportCategory",
] as const
export type CopyFieldRole = (typeof COPY_FIELD_ROLES)[number]

// The three fields every copy record must carry. The rest are optional —
// present only when a marketplace's prompt + attributeCodes ask for them. A
// field-scoped generation requesting a subset still always includes configSku
// plus its selected roles; these are the roles that are required when selected
// *and* part of the unscoped default.
export const REQUIRED_COPY_FIELD_ROLES = [
  "title",
  "shortDescription",
  "longDescription",
] as const satisfies readonly CopyFieldRole[]
export type RequiredCopyFieldRole = (typeof REQUIRED_COPY_FIELD_ROLES)[number]

const COPY_FIELD_ROLE_SET: ReadonlySet<string> = new Set(COPY_FIELD_ROLES)
const REQUIRED_COPY_FIELD_ROLE_SET: ReadonlySet<string> = new Set(REQUIRED_COPY_FIELD_ROLES)

export function isCopyFieldRole(value: string): value is CopyFieldRole {
  return COPY_FIELD_ROLE_SET.has(value)
}

/** Parses a comma-separated `--fields` value into a validated, de-duplicated,
 *  canonically-ordered field-role set. Throws on any unknown role so a typo
 *  fails loudly instead of silently generating the full set. An empty/blank
 *  value yields `undefined` (caller treats undefined as "all fields"). */
export function parseCopyFields(raw: string | undefined): readonly CopyFieldRole[] | undefined {
  if (raw === undefined) {
    return undefined
  }
  const requested = raw
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
  if (requested.length === 0) {
    return undefined
  }
  const unknown = requested.filter((token) => !isCopyFieldRole(token))
  if (unknown.length > 0) {
    throw new Error(
      `Unknown copy field(s): ${unknown.join(", ")}. Valid fields: ${COPY_FIELD_ROLES.join(", ")}.`
    )
  }
  const selected = new Set(requested)
  // Return in canonical order so downstream artifacts (schema, prompt) are
  // deterministic regardless of the order the operator typed.
  return COPY_FIELD_ROLES.filter((role) => selected.has(role))
}

// Every copy attribute is structurally optional so a field-scoped record (e.g.
// a title-only regeneration) validates without carrying the other fields. A
// full, unscoped generation still emits title/shortDescription/longDescription
// — nothing about the default path changes — but the *presence* of the selected
// fields is enforced per-request against the chosen field set rather than baked
// into this shared shape: `buildCopyBatchParseSchema` (packages/shared/src/copy/fields.ts)
// requires the selected core roles as `z.string().min(1)` when parsing the
// provider response, and the evaluator's `*_empty` checks
// (packages/shared/src/copy/validate.ts) reject empty selected fields.
// The shape is DERIVED from COPY_FIELD_ROLES — every role is an optional,
// non-empty string — so the const is the genuine single source of truth: adding
// a role grows this schema automatically, and the schema can never list a role
// the const doesn't. The cast bridges Object.fromEntries' loss of literal key
// types and is the one place it lives.
const copyAttributeValueShape = Object.fromEntries(
  COPY_FIELD_ROLES.map((role) => [role, z.string().min(1).optional()])
) as Record<CopyFieldRole, z.ZodOptional<z.ZodString>>

export const copyAttributesSchema = z.object(copyAttributeValueShape)
export type CopyAttributes = z.infer<typeof copyAttributesSchema>

/** The existing saved copy for an exact target, mapped back from the marketplace's
 *  native attribute codes to neutral roles. Rendered into a read-only
 *  `<current_copy>` block on a partial regeneration so the model stays coherent
 *  with the fields it is NOT regenerating (e.g. a title-only run sees the
 *  current longDescription and avoids restating it). Every role is optional —
 *  a target may have no prior copy at all. Structurally identical to
 *  `copyAttributesSchema`, so it reuses it rather than cloning the shape. */
export const copyCurrentValuesSchema = copyAttributesSchema
export type CopyCurrentValues = z.infer<typeof copyCurrentValuesSchema>

// The role → native-attribute-code map (below, in marketplaceCopyConfigSchema)
// is DERIVED from the same const: the three REQUIRED_COPY_FIELD_ROLES are
// mandatory codes, the rest optional. Keeping it derived means a new role can't
// be silently unmappable. The cast bridges Object.fromEntries' lost key types.
type CopyAttributeCodeShape = {
  [Role in CopyFieldRole]: Role extends RequiredCopyFieldRole
    ? z.ZodString
    : z.ZodOptional<z.ZodString>
}
const copyAttributeCodeShape = Object.fromEntries(
  COPY_FIELD_ROLES.map((role) => [
    role,
    REQUIRED_COPY_FIELD_ROLE_SET.has(role) ? z.string().min(1) : z.string().min(1).optional(),
  ])
) as CopyAttributeCodeShape

export const copyPreparationEvidenceSchema = z.object({
  descriptionNutritionHashes: z.record(z.string(), z.string()).optional(),
  sourceHash: z.string().min(1),
  category: z.string().min(1),
  fieldDeclarations: z.array(
    z.object({
      role: z.enum(COPY_FIELD_ROLES),
      attrCode: z.string().min(1),
      scope: z.enum(["config", "simple"]),
    })
  ),
  ownership: z.array(
    z.object({
      simpleSku: z.string().nullable(),
      attrCode: z.string().min(1),
      value: z.string(),
      source: z.string().min(1),
    })
  ),
})
export type CopyPreparationEvidence = z.infer<typeof copyPreparationEvidenceSchema>

export const copyPreparationSchema = z.object({
  descriptionNutrition: z.record(z.string(), z.unknown()).optional(),
  evidence: copyPreparationEvidenceSchema,
})
export type CopyPreparation = z.infer<typeof copyPreparationSchema>

export const copyInputItemSchema = z.object({
  configSku: z.string().min(1),
  /** Absent means the parent target; present is an exact catalog member. */
  simpleSku: z.string().min(1).optional(),
  fields: z.array(z.enum(COPY_FIELD_ROLES)).min(1).optional(),
  evidence: copyPreparationEvidenceSchema.optional(),
  name: z.string().min(1),
  category: z.string(),
  facts: z.unknown(),
  factsHash: z.string().min(1),
  // The config's existing applied copy, for coherence on a partial regen.
  // Present only when the run already has copy for this config; absent items
  // render an empty <current_copy> block.
  currentCopy: copyCurrentValuesSchema.optional(),
})
export type CopyInputItem = z.infer<typeof copyInputItemSchema>

export const copyInputFileSchema = z.object({
  schemaVersion: z.literal("copy_input_v3", {
    error: "Incompatible copy input; run nova copy prepare again.",
  }),
  generatedAt: isoDate,
  marketplace: marketplaceIdSchema,
  runSlug: z.string().min(1),
  items: z.array(copyInputItemSchema),
})
export type CopyInputFile = z.infer<typeof copyInputFileSchema>

// A committed regression set: a stable, hand-curated list of config SKUs reused
// across prompt-tuning sessions so a before/after comparison is a true
// comparison (same products every time). Lives next to the prompt it exercises
// at `data/prompts/<marketplace>/<category>.review-set.json`. Marketplace-
// neutral shape; `--review-set` loads it, `--sample N` pads it with diversity.
export const copyReviewSetFileSchema = z.object({
  schemaVersion: z.literal("copy_review_set_v1"),
  // Free-form note on what the set covers (capsules/powder/liquid, flagged
  // cases, …). Documentation only; ignored by the loader.
  description: z.string().optional(),
  configSkus: z.array(z.string().min(1)).min(1),
})
export type CopyReviewSetFile = z.infer<typeof copyReviewSetFileSchema>

export const copyGenerationMetadataSchema = z.object({
  promptId: z.string().min(1),
  provider: z.string().min(1),
  model: z.string().min(1),
  batchId: z.string().min(1),
})
export type CopyGenerationMetadata = z.infer<typeof copyGenerationMetadataSchema>

export const copyRecordSchema = z.object({
  configSku: z.string().min(1),
  simpleSku: z.string().min(1).optional(),
  fields: z.array(z.enum(COPY_FIELD_ROLES)).min(1).optional(),
  evidence: copyPreparationEvidenceSchema.optional(),
  attributes: copyAttributesSchema,
  generation: copyGenerationMetadataSchema,
})
export type CopyRecord = z.infer<typeof copyRecordSchema>

export const copyDraftFileSchema = z.object({
  schemaVersion: z.literal("copy_draft_v3", {
    error: "Incompatible copy draft; prepare, generate and review copy again.",
  }),
  generatedAt: isoDate,
  marketplace: marketplaceIdSchema,
  runSlug: z.string().min(1),
  provider: z.string().min(1),
  promptId: z.string().min(1),
  records: z.array(copyRecordSchema),
})
export type CopyDraftFile = z.infer<typeof copyDraftFileSchema>

// NOTE: the copy batch output contract is now built per-request from the
// selected field set (packages/shared/src/copy/fields.ts) — both the JSON schema given
// to `codex exec --output-schema` and the zod schema used to parse the
// response. A field-scoped batch only returns the selected fields, so there is
// no single static shape to share here.

export const copyEvaluationIssueSchema = z.object({
  severity: z.enum(["error", "warning"]),
  code: z.string().min(1),
  message: z.string().min(1),
})
export type CopyEvaluationIssue = z.infer<typeof copyEvaluationIssueSchema>

export const copyEvaluationItemSchema = z.object({
  configSku: z.string().min(1),
  simpleSku: z.string().min(1).optional(),
  status: z.enum(["pass", "warn", "fail"]),
  issues: z.array(copyEvaluationIssueSchema),
})
export type CopyEvaluationItem = z.infer<typeof copyEvaluationItemSchema>

export const copyEvaluationFileSchema = z.object({
  schemaVersion: z.literal("copy_eval_v3", {
    error: "Incompatible copy evaluation; evaluate the current prepared draft again.",
  }),
  generatedAt: isoDate,
  marketplace: marketplaceIdSchema,
  runSlug: z.string().min(1),
  summary: z.object({
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    warned: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  items: z.array(copyEvaluationItemSchema),
})
export type CopyEvaluationFile = z.infer<typeof copyEvaluationFileSchema>

// Marketplace `copy` block. Drives the CLI copy pipeline; the API loader also
// parses it (see apps/api config-schema) to surface the role → attribute-code
// mapping on the SKU preview. Lives inside the marketplace's `config.json`.
export const marketplaceCopyConfigSchema = z.object({
  promptFile: z.string().min(1).optional(),
  /** Output format the generated longDescription is authored in. Decathlon
   *  renders Markdown (the renderer converts it); Shop Apotheke's PDP renders
   *  raw HTML. Drives the copy evaluator: a `markdown` long description is
   *  rejected for containing HTML and warned for lacking markdown structure,
   *  while an `html` one is the reverse. Defaults to `markdown`. */
  /** How the marketplace RENDERS the long-description field, which decides what
   *  markup may be written into it.
   *
   *  - `markdown` — the field is rendered as Markdown (Decathlon).
   *  - `html` — the PDP renders raw HTML and expects it (Shop Apotheke).
   *  - `plain` — the field is rendered verbatim. Cdiscount does this: a `##`
   *    heading reaches the shopper as two hash marks mid-sentence and a `- `
   *    list collapses to a run-on line, which is why every competitor listing
   *    there is flowing prose. Same "no HTML" rule as markdown, but no
   *    structural markup is expected either, so the missing-structure warning
   *    is not raised.
   *
   *  Defaults to `markdown` — the historical behaviour for a marketplace that
   *  declares nothing. */
  longDescriptionFormat: z.enum(["markdown", "html", "plain"]).default("markdown"),
  /** Strict rich-text contract, checked before persistence and artifact output.
   * Tags must be bare, balanced, and contain no Markdown. Enrolled copy limits
   * reject overlong fields rather than truncating stored prose. */
  htmlAllowedTags: z
    .array(z.string().regex(/^[a-z][a-z0-9]*$/))
    .min(1)
    .optional(),
  /** Require every long-description node to live inside a top-level paragraph.
   * This is stricter than the general HTML allowlist because some marketplaces
   * accept top-level lists alongside paragraphs. */
  htmlParagraphsOnly: z.boolean().optional(),
  /** Final composed output may differ from the saved editorial input format. */
  outputHtmlAllowedTags: z
    .array(z.string().regex(/^[a-z][a-z0-9]*$/))
    .min(1)
    .optional(),
  attributeCodes: z.object(copyAttributeCodeShape),
  /** Additional schema attributes that must mirror the generated title (writer
   *  drafts propose a `copy` rule from the title attribute code). Declared per
   *  marketplace — e.g. Decathlon's `mainTitle` — so raw source product names
   *  (which often embed brand/color) never leak into title columns. */
  titleMirrorAttrCodes: z.array(z.string().min(1)).optional(),
  /** Separate website display rows; the copy title retains its product/URL role. */
  titleRows: z
    .object({
      firstAttrCode: z.string().min(1),
      secondAttrCode: z.string().min(1),
      firstMaxChars: z.number().int().positive(),
    })
    .optional(),
  /** Opt into strict title validation before save and after feed transforms.
   * Variant identity occupies the final flavour and net-content components. */
  titlePolicy: z
    .object({
      requireBrand: z.boolean(),
      variantSuffixSeparator: z
        .string()
        .refine((value) => value.trim().length > 0)
        .optional(),
      forbiddenCharacters: z.string().optional(),
      variantSuffixByCategory: z
        .record(
          z.string().min(1),
          z.object({
            requireFlavor: z.boolean(),
            capsuleFormLabels: z.array(z.string().min(1)),
            netWeightFallbackFormLabels: z.array(z.string().min(1)),
          })
        )
        .optional(),
    })
    .optional(),
  limits: z.object({
    titleMaxChars: z.number().int().positive(),
    shortDescriptionMaxChars: z.number().int().positive(),
    longDescriptionMaxChars: z.number().int().positive(),
  }),
})
export type MarketplaceCopyConfig = z.infer<typeof marketplaceCopyConfigSchema>

// Per-field character-length band, derived from a competitor sample.
// `minChars` is a hard floor (error when below); `warnMinChars` / `warnMaxChars`
// describe the competitor interquartile band (warning when outside). Hard max
// stays in `MarketplaceCopyConfig.limits`.
export const copyLengthBandSchema = z.object({
  minChars: z.number().int().positive().optional(),
  warnMinChars: z.number().int().positive().optional(),
  warnMaxChars: z.number().int().positive().optional(),
})
export type CopyLengthBand = z.infer<typeof copyLengthBandSchema>

// Fraction (0..1) of the competitor sample whose long description uses each
// structural element. Lets the evaluator warn "82% of competitor listings use a
// bullet list" when ours doesn't — copy structure, not just length.
export const copyLongStructureSchema = z.object({
  bulletsPct: z.number().min(0).max(1).optional(),
  tablePct: z.number().min(0).max(1).optional(),
  headingsPct: z.number().min(0).max(1).optional(),
  qnaPct: z.number().min(0).max(1).optional(),
})
export type CopyLongStructure = z.infer<typeof copyLongStructureSchema>

export const copyBenchmarkFieldsSchema = z.object({
  title: z.object({ length: copyLengthBandSchema.optional() }).optional(),
  shortDescription: z.object({ length: copyLengthBandSchema.optional() }).optional(),
  longDescription: z
    .object({
      length: copyLengthBandSchema.optional(),
      structure: copyLongStructureSchema.optional(),
    })
    .optional(),
})
export type CopyBenchmarkFields = z.infer<typeof copyBenchmarkFieldsSchema>

// Category-scoped artifact written by `nova copy calibrate` and read by the copy
// evaluator. Lives at
// `data/marketplaces/<tenant>/<country>/<categoryType>/copy-benchmark.json`.
// Holds the DERIVED, reviewable benchmark (length bands + structure prevalence),
// not the raw competitor listings — those remain PLAN-competitor-verification's
// job (a DB table). This file is policy and is meant to be committed.
export const copyBenchmarkFileSchema = z.object({
  schemaVersion: z.literal("copy_benchmark_v1"),
  marketplace: marketplaceIdSchema,
  categoryType: categoryTypeSchema,
  source: z.object({
    competitor: z.string().min(1),
    scrapedAt: isoDate,
    sampleSize: z.number().int().nonnegative(),
    note: z.string().optional(),
  }),
  fields: copyBenchmarkFieldsSchema,
})
export type CopyBenchmarkFile = z.infer<typeof copyBenchmarkFileSchema>
